import type { WorkspaceMember } from '@protoss/shared';
import { Button, DotsVerticalIcon, DropdownMenu, IconSize, PlusIcon } from '@protoss/ui';
import * as React from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { DomainForm } from '../components/organization/DomainForm';
import { DomainAdminInfo, ProjectAdminInfo } from '../components/organization/DomainMembersPanel';
import { ProjectForm } from '../components/organization/ProjectForm';
import { AppShell, MainContentShell, OrganizationTree } from '../components/shell';
import { useAppContext } from '../contexts/AppContext';
import { useLayout, useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import { apiFetch } from '../lib/apiClient';
import './home-page.css';

/**
 * HomePageContent - home page implementation with AppShell
 *
 * Default behavior (requirements §9):
 * - LeftNavPane visible
 * - ContextPane hidden
 * - RightUtilityPane hidden
 * - BottomPane hidden
 */
function HomePageContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{
    domainUuid?: string;
    projectUuid?: string;
  }>();

  const {
    workspace,
    domains: domainsData,
    isLoadingDomains,
    refetchDomains,
    activeModule,
  } = useWorkspace();
  const { currentDomain, currentProject, isDomainView, isProjectView } = useAppContext();
  const { setContextPaneVisible, contextPaneVisible } = useLayout();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);

  // Declarative base layout: context pane defaults hidden and is guaranteed to
  // reset on unmount. The data-gated effect below opens it for entity/root views.
  useLayoutConfig({ showContextPane: false });

  // Project preview - selected via org tree click, persisted in ?preview= query param
  const [searchParams, setSearchParams] = useSearchParams();
  const previewProjectId = searchParams.get('preview') ?? undefined;

  const previewProject = React.useMemo(() => {
    if (!previewProjectId) return undefined;
    for (const domain of domainsData) {
      const project = domain.projects.find((p) => p.uuid === previewProjectId);
      if (project) return { ...project, domainName: domain.name, domainUuid: domain.uuid };
    }
    return undefined;
  }, [previewProjectId, domainsData]);

  //Parse URL to determine current view
  const currentPath = location.pathname;
  const isCreateDomain = currentPath === '/do/domain/new';
  const isEditDomain = currentPath.endsWith('/edit') && params.domainUuid;
  const isCreateProject = currentPath.endsWith('/project/new');
  const isEditProject = currentPath.endsWith('/edit') && params.projectUuid;

  // Extract preselected domain for project creation from URL
  const selectedDomainForProject = isCreateProject ? params.domainUuid : undefined;

  // Org tree highlight: domain deselects when a project preview is active
  const selectedDomainId = isDomainView && !previewProjectId ? params.domainUuid : undefined;
  const selectedProjectId = previewProjectId;

  const [workspaceMembers, setWorkspaceMembers] = React.useState<WorkspaceMember[]>([]);

  // Fetch workspace members when the create or edit domain/project form is accessed
  React.useEffect(() => {
    if (!isCreateDomain && !isEditDomain && !isCreateProject && !isEditProject) return;
    let cancelled = false;

    const fetchMembers = async () => {
      try {
        const data = await apiFetch<{ members: WorkspaceMember[] }>('/api/workspace/members');
        if (!cancelled) {
          setWorkspaceMembers(data.members ?? []);
        }
      } catch {
        // Non-critical: form will show empty selector
      }
    };

    void fetchMembers();
    return () => {
      cancelled = true;
    };
  }, [isCreateDomain, isEditDomain, isCreateProject, isEditProject]);

  // Auto-open context pane when viewing domain/project details or root organization view
  // Wait for data to load to prevent flashing
  React.useEffect(() => {
    const isEntityView = isDomainView || isProjectView || !!previewProjectId;
    const moduleRoot = activeModule?.defaultRoute ?? '/';
    const isRootPath = currentPath === '/' || currentPath === moduleRoot;

    // Open context pane for entity views or root organization view, but only after data loads
    if ((isEntityView || isRootPath) && !isLoadingDomains) {
      setContextPaneVisible(true);
    }
  }, [
    isDomainView,
    isProjectView,
    previewProjectId,
    currentPath,
    activeModule,
    isLoadingDomains,
    setContextPaneVisible,
  ]);

  // Form submission handlers
  const handleSubmitDomainCreate = async (data: {
    name?: string;
    description?: string;
    domainAdminUuids?: string[];
  }) => {
    const result = await apiFetch<{ domain: { uuid: string } }>('/api/domain', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await refetchDomains();
    navigate(`/do/domain/${result.domain.uuid}`);
  };

  const handleSubmitDomainEdit = async (data: {
    name?: string;
    description?: string;
    domainAdminUuids?: string[];
  }) => {
    if (!params.domainUuid) throw new Error('No domain selected');
    await apiFetch(`/api/domain/${params.domainUuid}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await refetchDomains();
    navigate(`/do/domain/${params.domainUuid}`);
  };

  const handleSubmitProject = async (data: {
    domainUuid?: string;
    name?: string;
    description?: string;
    projectAdminUuids?: string[];
  }) => {
    const result = await apiFetch<{ project: { uuid: string } }>('/api/project', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await refetchDomains();
    navigate(`/do/project/${result.project.uuid}`);
  };

  const handleSubmitProjectEdit = async (data: {
    name?: string;
    description?: string;
    projectAdminUuids?: string[];
  }) => {
    if (!params.projectUuid) throw new Error('No project selected');
    await apiFetch(`/api/project/${params.projectUuid}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    await refetchDomains();
    navigate(`/do/project/${params.projectUuid}`);
  };

  // LeftNavPane content - reuses shared AppNav component
  const leftNav = (
    <AppNav
      selected="primary"
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
      onPrimaryClick={() => setContextPaneVisible(true)}
    />
  );

  // ContextPane content - Organization tree
  const contextPane = (
    <OrganizationTree
      domains={domainsData.map((domain) => ({
        id: domain.uuid,
        name: domain.name,
        projects: domain.projects.map((project) => ({
          id: project.uuid,
          name: project.name,
        })),
      }))}
      selectedDomainId={selectedDomainId}
      selectedProjectId={selectedProjectId}
      isWorkspaceAdmin={isWorkspaceAdmin}
      onDomainSelect={(domainId) => navigate(`/do/domain/${domainId}`)}
      onProjectSelect={(projectId) => setSearchParams({ preview: projectId })}
      onCreateDomain={() => navigate('/do/domain/new')}
      onCreateProject={(domainId) =>
        domainId ? navigate(`/do/domain/${domainId}/project/new`) : navigate('/do/project/new')
      }
    />
  );

  // MainPane content
  const main = (
    <>
      {isCreateDomain && (
        <>
          <div className="main-pane-header">
            <h1 className="main-pane-title">Create Domain</h1>
          </div>
          <div className="main-pane-content">
            <DomainForm
              mode="create"
              workspaceMembers={workspaceMembers}
              onSubmit={handleSubmitDomainCreate}
              onCancel={() => navigate('/do')}
            />
          </div>
        </>
      )}
      {isEditDomain && currentDomain && (
        <>
          <div className="main-pane-header">
            <h1 className="main-pane-title">Edit Domain</h1>
          </div>
          <div className="main-pane-content">
            <DomainForm
              mode="edit"
              initialData={{
                name: currentDomain.name,
                description: currentDomain.description,
              }}
              workspaceMembers={workspaceMembers}
              initialAdmins={currentDomain.admins}
              onSubmit={handleSubmitDomainEdit}
              onCancel={() => navigate(`/do/domain/${params.domainUuid}`)}
            />
          </div>
        </>
      )}
      {isCreateProject && (
        <>
          <div className="main-pane-header">
            <h1 className="main-pane-title">Create Project</h1>
          </div>
          <div className="main-pane-content">
            <ProjectForm
              mode="create"
              domains={domainsData.map((d) => ({ uuid: d.uuid, name: d.name, admins: d.admins }))}
              preselectedDomainUuid={selectedDomainForProject}
              workspaceMembers={workspaceMembers}
              onSubmit={handleSubmitProject}
              onCancel={() =>
                selectedDomainForProject
                  ? navigate(`/do/domain/${selectedDomainForProject}`)
                  : navigate('/do')
              }
            />
          </div>
        </>
      )}
      {isEditProject && currentProject && (
        <>
          <div className="main-pane-header">
            <h1 className="main-pane-title">Edit Project</h1>
          </div>
          <div className="main-pane-content">
            <ProjectForm
              mode="edit"
              domains={[]}
              workspaceMembers={workspaceMembers}
              initialAdmins={currentProject.admins}
              initialData={{
                name: currentProject.name,
                description: currentProject.description,
                domainUuid: currentProject.domainUuid,
              }}
              onSubmit={handleSubmitProjectEdit}
              onCancel={() => navigate(`/do/project/${params.projectUuid}`)}
            />
          </div>
        </>
      )}
      {previewProject && !isCreateDomain && !isEditDomain && !isCreateProject && !isEditProject && (
        <MainContentShell
          title={previewProject.name}
          breadcrumb={previewProject.domainName}
          actions={
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => navigate(`/do/project/${previewProject.uuid}`)}
            >
              Open
            </Button>
          }
        >
          <div className="entity-info__section">
            <h3 className="entity-info__section-title">Domain</h3>
            <p className="entity-info__stat">{previewProject.domainName}</p>
          </div>

          <div className="entity-info__section">
            <h3 className="entity-info__section-title">Description</h3>
            {previewProject.description ? (
              <p className="entity-info__description">{previewProject.description}</p>
            ) : (
              <p className="entity-info__empty">No description yet.</p>
            )}
          </div>

          <div className="entity-info__section">
            <h3 className="entity-info__section-title">Administrators</h3>
            <ProjectAdminInfo admins={previewProject.admins} />
          </div>
        </MainContentShell>
      )}
      {isDomainView && !isEditDomain && !isCreateProject && !previewProject && currentDomain && (
        <MainContentShell
          title={currentDomain.name}
          breadcrumb="Domain"
          canEdit={isWorkspaceAdmin}
          onEdit={() => navigate(`/do/domain/${currentDomain.uuid}/edit`)}
        >
          <div className="entity-info__section">
            <h3 className="entity-info__section-title">Administrators</h3>
            <DomainAdminInfo admins={currentDomain.admins} />
          </div>

          <div className="entity-info__section">
            <h3 className="entity-info__section-title">Description</h3>
            {currentDomain.description ? (
              <p className="entity-info__description">{currentDomain.description}</p>
            ) : (
              <p className="entity-info__empty">No description yet.</p>
            )}
          </div>

          <div className="entity-info__section">
            <h3 className="entity-info__section-title">Projects</h3>
            <p className="entity-info__stat">
              {currentDomain.projects.length === 0
                ? 'No projects yet'
                : `${currentDomain.projects.length} project${
                    currentDomain.projects.length === 1 ? '' : 's'
                  }`}
            </p>
          </div>
        </MainContentShell>
      )}
      {/* /project/:uuid now handled by ProjectPage (see App.tsx) */}
      {!isCreateDomain &&
        !isEditDomain &&
        !isCreateProject &&
        !isEditProject &&
        !isDomainView &&
        !isProjectView &&
        !previewProject && (
          <>
            {contextPaneVisible ? (
              // Show organization overview when context pane is open
              <MainContentShell
                title="Organization"
                actions={
                  isWorkspaceAdmin ? (
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <button
                          type="button"
                          className="entity-action-button"
                          aria-label="Organization actions"
                        >
                          <DotsVerticalIcon
                            width={IconSize.md}
                            height={IconSize.md}
                            aria-hidden="true"
                          />
                        </button>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Portal>
                        <DropdownMenu.Content
                          className="entity-action-menu dropdown-content"
                          side="bottom"
                          align="end"
                          sideOffset={4}
                        >
                          <DropdownMenu.Item
                            className="entity-action-menu-item dropdown-item"
                            onSelect={() => navigate('/do/domain/new')}
                          >
                            <PlusIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
                            Create Domain
                          </DropdownMenu.Item>
                          <DropdownMenu.Item
                            className="entity-action-menu-item dropdown-item"
                            onSelect={() => navigate('/do/project/new')}
                          >
                            <PlusIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
                            Create Project
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                  ) : undefined
                }
              >
                {isLoadingDomains ? (
                  <div className="home-placeholder">
                    <p className="home-placeholder-body">Loading organization data...</p>
                  </div>
                ) : domainsData.length === 0 ? (
                  <div className="home-placeholder">
                    <h2 className="home-placeholder-heading">No domains yet</h2>
                    <p className="home-placeholder-body">
                      {isWorkspaceAdmin
                        ? 'Create a domain to get started organizing your projects.'
                        : 'Contact your workspace administrator to create domains.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {domainsData.map((domain) => (
                      <div key={domain.uuid} className="org-domain-section">
                        <div className="org-domain-header">
                          <h2 className="org-domain-title">
                            <a
                              href={`/do/domain/${domain.uuid}`}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/do/domain/${domain.uuid}`);
                              }}
                              className="org-domain-link"
                            >
                              {domain.name}
                            </a>
                          </h2>
                          {domain.description && (
                            <p className="org-domain-description">{domain.description}</p>
                          )}
                        </div>
                        {domain.projects.length > 0 ? (
                          <div className="org-projects-grid">
                            {domain.projects.map((project) => (
                              <div
                                key={project.uuid}
                                className="org-project-card"
                                onClick={() => navigate(`/do/project/${project.uuid}`)}
                              >
                                <h3 className="org-project-title">{project.name}</h3>
                                {project.description && (
                                  <p className="org-project-description">{project.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="org-no-projects">No projects yet</p>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </MainContentShell>
            ) : (
              // Show simple workspace view when context pane is closed
              <>
                <div className="main-pane-header">
                  <h1 className="main-pane-title">Workspace</h1>
                </div>
                <div className="main-pane-content">
                  <div className="home-placeholder">
                    <h2 className="home-placeholder-heading">Main Workspace</h2>
                    <p className="home-placeholder-body">
                      {isLoadingDomains
                        ? 'Loading organization data...'
                        : domainsData.length === 0
                          ? 'No domains yet. Create a domain to get started.'
                          : 'Click Organization to browse domains and projects.'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </>
        )}
    </>
  );

  return (
    <AppShell
      leftNav={leftNav}
      contextPane={contextPane}
      main={main}
      suppressModuleChat
      // bottomPane and rightPane are undefined/omitted
      // which means they are hidden by default (per requirements §9)
    />
  );
}

/**
 * HomePage - protected multi-pane shell
 *
 * Auth is enforced at the router level via ProtectedRoute in App.tsx.
 * This component only renders when the user is authenticated.
 *
 * Layout follows the Protoss frontend architecture:
 *   LeftNavPane | ContextPane | MainPane | RightUtilityPane
 *   MainPane also supports an internal BottomPane separated by horizontal divider
 */
export function HomePage() {
  return <HomePageContent />;
}
